import { ActionNode, cleanupActionNode, DATA_ACTION_NODE, handleActionNode } from "./utilty";

const SHADOW_HOST_SELECTOR = '[shadow]';

const isActionNode = (node: HTMLElement): node is ActionNode => node.matches(DATA_ACTION_NODE);

const forEachActionNode = (root: HTMLElement | ShadowRoot, fn: (node: ActionNode) => void) => {
  if (root instanceof HTMLElement && isActionNode(root)) {
    fn(root);
  }

  root
    .querySelectorAll<ActionNode>(DATA_ACTION_NODE)
    .forEach(fn);
};

const forEachShadowRoot = (root: HTMLElement | ShadowRoot, fn: (shadowRoot: ShadowRoot) => void) => {
  if (root instanceof HTMLElement && root.hasAttribute('shadow') && root.shadowRoot) {
    fn(root.shadowRoot);
  }

  root
    .querySelectorAll<HTMLElement>(SHADOW_HOST_SELECTOR)
    .forEach((host) => host.shadowRoot && fn(host.shadowRoot));
};

const registerActionNodes = (root: HTMLElement | ShadowRoot) => {
  const observed = new WeakSet<Node>();
  const observers = new WeakMap<Node, MutationObserver>();
  const activeObservers = new Set<MutationObserver>();

  const disconnect = (node: Node) => {
    const observer = observers.get(node);
    if (!observer) return;
    observer.disconnect();
    observers.delete(node);
    activeObservers.delete(observer);
  };

  const cleanupShadowTree = (shadowRoot: ShadowRoot) => {
    forEachActionNode(shadowRoot, cleanupActionNode);
    forEachShadowRoot(shadowRoot, cleanupShadowTree);
    disconnect(shadowRoot);
  };

  const handleAdded = (node: HTMLElement) => {
    forEachActionNode(node, handleActionNode);
    forEachShadowRoot(node, watch);
  };

  const handleRemoved = (node: HTMLElement) => {
    forEachActionNode(node, cleanupActionNode);
    forEachShadowRoot(node, cleanupShadowTree);
  };

  const watch = (node: HTMLElement | ShadowRoot) => {
    if (observed.has(node)) return;
    observed.add(node);

    forEachActionNode(node, handleActionNode);
    forEachShadowRoot(node, watch);

    const mo = new MutationObserver(muts => {
      for (const m of muts) {
        for (const added of Array.from(m.addedNodes)) {
          if (!(added instanceof HTMLElement)) continue;
          handleAdded(added);
        }
        for (const removed of Array.from(m.removedNodes)) {
          if (!(removed instanceof HTMLElement)) continue;
          handleRemoved(removed);
        }
      }
    });

    mo.observe(node, {
      childList: true,
      subtree  : true
    });
    observers.set(node, mo);
    activeObservers.add(mo);
  };

  watch(root);
  return () => {
    for (const observer of activeObservers) {
      observer.disconnect();
    }
    activeObservers.clear();
  };
}

export { registerActionNodes };
